/**
 * <BrandsDashboardTab /> — cross-brand rollup shown on Brands > Dashboard.
 * Uses `/workspaces/{ws}/brands-dashboard`.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import EmptyState from "@/components/EmptyState";
import { Gem, Package, FolderKanban, Layers, Sparkles } from "lucide-react";

function Stat({ icon: Icon, label, value, tone = "", testid }) {
  return (
    <div className="border border-border p-4 space-y-1" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <div className={`text-2xl font-bold font-mono leading-tight ${tone}`}>{value}</div>
    </div>
  );
}

const STATUS_TONE = {
  active: "border-emerald-500/40 text-emerald-700 bg-emerald-500/5",
  paused: "border-amber-500/40 text-amber-700 bg-amber-500/5",
  archived: "border-border text-muted-foreground bg-secondary/40",
};

export default function BrandsDashboardTab() {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!activeWorkspaceId) return;
    setLoading(true);
    api.get(`/workspaces/${activeWorkspaceId}/brands-dashboard`)
      .then((r) => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [activeWorkspaceId]);

  if (loading) return <div className="text-sm text-muted-foreground py-6">Loading dashboard…</div>;
  if (!data || data.totals.brands === 0) {
    return <EmptyState title="No brand data yet" description="Create a brand to see the dashboard." icon={Gem} testid="brands-dashboard-empty" />;
  }

  const t = data.totals;
  const industries = Object.entries(data.industry_mix || {}).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const maxAdded = Math.max(1, ...(data.added_weekly || []).map((w) => w.count));

  return (
    <div className="space-y-6" data-testid="brands-dashboard-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Gem} label="Brands" value={t.brands} testid="stat-brands" />
        <Stat icon={Sparkles} label="Active" value={t.active} tone={t.active ? "text-emerald-600" : ""} testid="stat-active" />
        <Stat icon={Package} label="With active packages" value={t.with_active_packages} testid="stat-with-packages" />
        <Stat icon={FolderKanban} label="With active projects" value={t.with_active_projects} testid="stat-with-projects" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-border p-6 space-y-4" data-testid="brands-status-mix">
          <div className="label-eyebrow">Status mix</div>
          <div className="space-y-1.5">
            {["active", "paused", "archived"].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-wider border px-1.5 py-0.5 ${STATUS_TONE[s]}`}>{s}</span>
                <div className="flex-1 h-1.5 bg-secondary">
                  <div
                    className={`h-full ${s === "active" ? "bg-emerald-500" : s === "paused" ? "bg-amber-500" : "bg-muted-foreground"}`}
                    style={{ width: `${Math.round(((data.status_mix?.[s] || 0) * 100) / Math.max(1, t.brands))}%` }}
                  />
                </div>
                <span className="text-xs font-mono w-6 text-right">{data.status_mix?.[s] || 0}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-border p-6 space-y-4" data-testid="brands-industries">
          <div className="label-eyebrow flex items-center gap-2"><Layers className="w-3.5 h-3.5" /> Industries</div>
          <div className="space-y-1.5">
            {industries.map(([name, count]) => (
              <div key={name} className="flex items-center gap-2">
                <span className="text-xs truncate flex-1">{name}</span>
                <div className="w-24 h-1.5 bg-secondary">
                  <div className="h-full bg-primary" style={{ width: `${(count * 100) / Math.max(...industries.map(([, c]) => c))}%` }} />
                </div>
                <span className="text-xs font-mono w-6 text-right">{count}</span>
              </div>
            ))}
            {industries.length === 0 && <div className="text-xs text-muted-foreground">No industry data yet.</div>}
          </div>
        </div>
      </div>

      <div className="border border-border p-6 space-y-3" data-testid="brands-added-timeline">
        <div className="label-eyebrow">Brands added — last 12 weeks</div>
        <div className="flex items-end gap-1 h-24">
          {(data.added_weekly || []).map((w, i) => (
            <div key={i} className="flex-1 bg-primary/70 hover:bg-primary transition" title={`Week of ${w.start}: ${w.count} added`} style={{ height: `${(w.count * 100) / maxAdded}%`, minHeight: w.count ? "4px" : "2px" }} />
          ))}
        </div>
      </div>

      <div className="border border-border p-6" data-testid="brands-top">
        <div className="label-eyebrow mb-3">Top brands by delivery activity</div>
        <div className="space-y-2">
          {(data.top || []).map((r) => (
            <Link key={r.id} to={`/app/brands/${r.id}`} className="flex items-center justify-between border border-border/60 hover:border-primary/50 p-3 transition group" data-testid={`brand-top-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate group-hover:text-primary transition">{r.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{r.industry || "—"}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0 text-xs">
                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${STATUS_TONE[r.status] || ""}`}>{r.status}</span>
                <span className="font-mono min-w-[60px] text-right">
                  <span className="font-bold">{r.active_packages}</span> pkg
                </span>
                <span className="font-mono min-w-[60px] text-right">
                  <span className="font-bold">{r.active_projects}</span> proj
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
